'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    QUICK_MESSAGE_VISIBILITY_COLORS,
    QUICK_MESSAGE_VISIBILITY_LABELS,
    QuickMessage,
    QuickMessageVisibility,
    deleteQuickMessage,
    getQuickMessages,
} from '@/lib/api/messaging/quick-messages-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdAttachFile,
    MdBolt,
    MdDelete,
    MdEdit,
    MdSearch,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const VISIBILITIES: QuickMessageVisibility[] = [
    'PRIVATE',
    'COMPANY',
    'ORGANIZATION',
]

/** Prévia de uma linha só: a tabela não é lugar de ler o texto inteiro. */
function preview(body: string): string {
    const flat = body.replace(/\s+/g, ' ').trim()
    return flat.length > 90 ? `${flat.slice(0, 90)}…` : flat
}

const QuickMessagesPage = () => {
    const { canRead, canCreate, canUpdate, canDelete } = useResourcePermissions(
        'messaging',
        'quick_messages'
    )
    const navigate = useNavigate()

    const [messages, setMessages] = useState<QuickMessage[]>([])
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState('')
    const [filters, setFilters] = useState<{
        active?: boolean
        visibility?: QuickMessageVisibility
        search?: string
    }>({})

    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [deletingId, setDeletingId] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    const fetchMessages = async () => {
        setLoading(true)
        try {
            const res = await getQuickMessages(filters)
            if (res.success) {
                setMessages(res.result)
            } else {
                toast.error(res.error || 'Erro ao carregar mensagens rápidas')
            }
        } catch {
            toast.error('Falha na requisição')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (canRead) fetchMessages()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canRead, JSON.stringify(filters)])

    // Busca só dispara depois que o operador para de digitar — a listagem vai
    // ao backend a cada mudança de filtro.
    useEffect(() => {
        const timer = setTimeout(() => {
            setFilters(f => ({ ...f, search: search.trim() || undefined }))
        }, 400)
        return () => clearTimeout(timer)
    }, [search])

    const handleDelete = async () => {
        if (deletingId == null) return
        setDeleting(true)
        try {
            const res = await deleteQuickMessage(deletingId)
            if (res.success) {
                toast.success('Mensagem rápida removida')
                setShowDeleteModal(false)
                setDeletingId(null)
                fetchMessages()
            } else {
                toast.error(res.error || 'Erro ao remover')
            }
        } finally {
            setDeleting(false)
        }
    }

    if (!canRead) {
        return (
            <div className="p-6">
                <div className="alert alert-warning">
                    Você não tem permissão para acessar mensagens rápidas.
                </div>
            </div>
        )
    }

    return (
        <div className="p-6 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h1 className="text-2xl font-bold">Mensagens rápidas</h1>
                    <p className="text-sm text-base-content/70">
                        Textos prontos que o atendente insere no chat digitando
                        <span className="font-mono mx-1 px-1 rounded bg-base-200">
                            /
                        </span>
                        seguido do atalho.
                    </p>
                </div>
                {canCreate && (
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm gap-2"
                        onClick={() => navigate('/messaging/quick-messages/new')}
                    >
                        <MdAdd size={18} /> Nova mensagem rápida
                    </button>
                )}
            </div>

            <div className="card bg-base-100 shadow-sm">
                <div className="card-body p-4 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <label className="flex flex-col gap-1 w-full sm:col-span-1">
                            <span className="text-sm font-medium text-base-content/80">
                                Buscar
                            </span>
                            <label className="input input-bordered input-sm flex items-center gap-2">
                                <MdSearch className="text-base-content/50" />
                                <input
                                    type="text"
                                    className="grow"
                                    placeholder="Atalho, título ou texto"
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                />
                            </label>
                        </label>
                        <label className="flex flex-col gap-1 w-full">
                            <span className="text-sm font-medium text-base-content/80">
                                Visibilidade
                            </span>
                            <select
                                className="select select-bordered select-sm"
                                value={filters.visibility ?? ''}
                                onChange={e =>
                                    setFilters(f => ({
                                        ...f,
                                        visibility: (e.target.value ||
                                            undefined) as
                                            | QuickMessageVisibility
                                            | undefined,
                                    }))
                                }
                            >
                                <option value="">Todas</option>
                                {VISIBILITIES.map(v => (
                                    <option key={v} value={v}>
                                        {QUICK_MESSAGE_VISIBILITY_LABELS[v]}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="flex flex-col gap-1 w-full">
                            <span className="text-sm font-medium text-base-content/80">
                                Ativas
                            </span>
                            <select
                                className="select select-bordered select-sm"
                                value={
                                    filters.active == null
                                        ? ''
                                        : filters.active
                                          ? 'true'
                                          : 'false'
                                }
                                onChange={e =>
                                    setFilters(f => ({
                                        ...f,
                                        active:
                                            e.target.value === ''
                                                ? undefined
                                                : e.target.value === 'true',
                                    }))
                                }
                            >
                                <option value="">Todas</option>
                                <option value="true">Apenas ativas</option>
                                <option value="false">Apenas inativas</option>
                            </select>
                        </label>
                    </div>

                    {loading ? (
                        <div className="flex justify-center p-8">
                            <span className="loading loading-spinner loading-md" />
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="text-center p-10 space-y-3">
                            <MdBolt
                                size={40}
                                className="mx-auto text-base-content/25"
                            />
                            <p className="text-base-content/60">
                                Nenhuma mensagem rápida cadastrada.
                            </p>
                            {canCreate && (
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm gap-2"
                                    onClick={() =>
                                        navigate('/messaging/quick-messages/new')
                                    }
                                >
                                    <MdAdd size={18} /> Criar a primeira
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table table-zebra table-sm">
                                <thead>
                                    <tr>
                                        <th>Atalho</th>
                                        <th>Título</th>
                                        <th>Texto</th>
                                        <th>Visibilidade</th>
                                        <th>Setor</th>
                                        <th className="text-right">Usos</th>
                                        <th className="text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {messages.map(msg => (
                                        <tr
                                            key={msg.id}
                                            className={
                                                msg.active ? '' : 'opacity-50'
                                            }
                                        >
                                            <td>
                                                <span className="font-mono text-sm px-2 py-1 rounded bg-base-200">
                                                    /{msg.trigger}
                                                </span>
                                            </td>
                                            <td className="font-medium">
                                                <div className="flex items-center gap-2">
                                                    {msg.title}
                                                    {msg.mediaUrls &&
                                                        msg.mediaUrls.length >
                                                            0 && (
                                                            <span
                                                                className="text-base-content/50"
                                                                title={`${msg.mediaUrls.length} anexo(s)`}
                                                            >
                                                                <MdAttachFile />
                                                            </span>
                                                        )}
                                                    {!msg.active && (
                                                        <span className="badge badge-ghost badge-sm">
                                                            Inativa
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="text-base-content/70 max-w-md">
                                                {preview(msg.body)}
                                            </td>
                                            <td>
                                                <span
                                                    className={`badge badge-sm ${
                                                        QUICK_MESSAGE_VISIBILITY_COLORS[
                                                            msg.visibility
                                                        ]
                                                    }`}
                                                >
                                                    {
                                                        QUICK_MESSAGE_VISIBILITY_LABELS[
                                                            msg.visibility
                                                        ]
                                                    }
                                                </span>
                                            </td>
                                            <td className="text-base-content/70">
                                                {msg.department?.name ?? 'Todos'}
                                            </td>
                                            <td className="text-right tabular-nums">
                                                {msg.usageCount}
                                            </td>
                                            <td>
                                                <div className="flex justify-end gap-1">
                                                    {canUpdate && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-ghost btn-xs btn-square"
                                                            title="Editar"
                                                            onClick={() =>
                                                                navigate(
                                                                    `/messaging/quick-messages/edit/${msg.id}`
                                                                )
                                                            }
                                                        >
                                                            <MdEdit size={16} />
                                                        </button>
                                                    )}
                                                    {canDelete && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-ghost btn-xs btn-square text-error"
                                                            title="Remover"
                                                            onClick={() => {
                                                                setDeletingId(
                                                                    msg.id
                                                                )
                                                                setShowDeleteModal(
                                                                    true
                                                                )
                                                            }}
                                                        >
                                                            <MdDelete
                                                                size={16}
                                                            />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setDeletingId(null)
                }}
                onConfirm={handleDelete}
                title="Remover mensagem rápida"
                message="O atalho deixa de aparecer no chat para todos que o enxergavam. As mensagens já enviadas não são afetadas."
                loading={deleting}
                confirmButtonText="Remover"
                cancelButtonText="Cancelar"
                confirmButtonClass="btn-error"
            />
        </div>
    )
}

export default QuickMessagesPage
